import { useEffect, useState } from 'react'
import { Loader2, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import type { QuoteV2, QuoteVersionV2 } from './types'
import { generateWorkOrderPdfFromQuotes } from './quotes-v2.api'

interface WorkOrderSelectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  quotes: QuoteV2[]
}

interface SelectionEntry {
  checked: boolean
  selectedVersionId: string | null
  /** All generated versions across all variants, newest first */
  eligibleVersions: QuoteVersionV2[]
  disabled: boolean
}

function formatVersionLabel(version: QuoteVersionV2): string {
  const base = `V${version.versionNumber}`
  return version.name?.trim() ? `${base} — ${version.name.trim()}` : base
}

function computeInitialSelections(quotes: QuoteV2[]): Map<string, SelectionEntry> {
  // Use status === 'accepted' — a version can have acceptedAt set but be superseded
  const hasAnyAccepted = quotes.some((q) =>
    q.variants.some((v) => v.generatedVersions.some((gv) => gv.status === 'accepted'))
  )

  const map = new Map<string, SelectionEntry>()

  quotes.forEach((quote) => {
    // Flatten generated versions across all variants, backend guarantees newest-first order per variant
    const allGenerated = quote.variants.flatMap((v) => v.generatedVersions)

    if (allGenerated.length === 0) {
      map.set(quote.id, {
        checked: false,
        selectedVersionId: null,
        eligibleVersions: [],
        disabled: true,
      })
      return
    }

    const acceptedVersion = allGenerated.find((gv) => gv.status === 'accepted') ?? null
    const latestVersion = allGenerated[0]

    let checked: boolean
    let selectedVersionId: string

    if (hasAnyAccepted) {
      if (acceptedVersion) {
        checked = true
        selectedVersionId = acceptedVersion.id
      } else {
        checked = false
        selectedVersionId = latestVersion.id
      }
    } else {
      checked = true
      selectedVersionId = latestVersion.id
    }

    map.set(quote.id, {
      checked,
      selectedVersionId,
      eligibleVersions: allGenerated,
      disabled: false,
    })
  })

  return map
}

export function WorkOrderSelectionDialog({
  open,
  onOpenChange,
  projectId,
  quotes,
}: WorkOrderSelectionDialogProps) {
  const [selections, setSelections] = useState<Map<string, SelectionEntry>>(new Map())
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-initialize selections each time the dialog opens.
  // useState only runs its initializer once at mount, and Dialog.onOpenChange
  // does not fire when the open prop is set to true from outside — so useEffect
  // is the only reliable way to reset state on open.
  useEffect(() => {
    if (open) {
      setSelections(computeInitialSelections(quotes))
      setError(null)
      setIsGenerating(false)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isGenerating) {
      onOpenChange(nextOpen)
    }
  }

  const toggleChecked = (quoteId: string) => {
    setSelections((prev) => {
      const next = new Map(prev)
      const entry = next.get(quoteId)
      if (entry && !entry.disabled) {
        next.set(quoteId, { ...entry, checked: !entry.checked })
      }
      return next
    })
  }

  const setVersion = (quoteId: string, versionId: string) => {
    setSelections((prev) => {
      const next = new Map(prev)
      const entry = next.get(quoteId)
      if (entry) {
        next.set(quoteId, { ...entry, selectedVersionId: versionId })
      }
      return next
    })
  }

  const activeSelections = [...selections.entries()]
    .filter(([, entry]) => entry.checked && entry.selectedVersionId != null)
    .map(([quoteId, entry]) => ({
      quoteId,
      quoteVersionId: entry.selectedVersionId as string,
    }))

  const canGenerate = activeSelections.length > 0 && !isGenerating

  const handleGenerate = async () => {
    if (!canGenerate) return
    setError(null)

    try {
      setIsGenerating(true)
      const blob = await generateWorkOrderPdfFromQuotes(projectId, activeSelections)
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `bordereau-${projectId}.pdf`
      a.click()
      URL.revokeObjectURL(blobUrl)
      onOpenChange(false)
    } catch {
      setError('Impossible de générer le bordereau. Veuillez réessayer.')
    } finally {
      setIsGenerating(false)
    }
  }

  const quoteLabel = (quote: QuoteV2, index: number): string => {
    if (quote.name?.trim()) return quote.name.trim()
    if (quote.documentNumber?.trim()) return quote.documentNumber.trim()
    return `Devis ${index + 1}`
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Générer un bordereau
          </DialogTitle>
          <DialogDescription>
            Sélectionnez les versions de devis à inclure dans le bordereau.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {quotes.map((quote, index) => {
            const entry = selections.get(quote.id)
            if (!entry) return null

            return (
              <div
                key={quote.id}
                className={`rounded-md border px-3 py-2.5 ${entry.disabled ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`quote-${quote.id}`}
                    checked={entry.checked}
                    disabled={entry.disabled}
                    onCheckedChange={() => toggleChecked(quote.id)}
                  />
                  <Label
                    htmlFor={`quote-${quote.id}`}
                    className={`flex-1 min-w-0 text-sm font-medium truncate ${entry.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {quoteLabel(quote, index)}
                    {entry.disabled && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        (brouillon uniquement)
                      </span>
                    )}
                  </Label>

                  {!entry.disabled && entry.eligibleVersions.length > 1 && (
                    <Select
                      value={entry.selectedVersionId ?? ''}
                      onValueChange={(v) => setVersion(quote.id, v)}
                      disabled={!entry.checked || isGenerating}
                    >
                      <SelectTrigger className="w-[160px] h-8 text-sm flex-shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {entry.eligibleVersions.map((version) => (
                          <SelectItem key={version.id} value={version.id}>
                            {formatVersionLabel(version)}
                            {version.statusLabel && (
                              <span className="ml-1.5 text-xs text-muted-foreground">
                                · {version.statusLabel}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {!entry.disabled && entry.eligibleVersions.length === 1 && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatVersionLabel(entry.eligibleVersions[0])}
                      {entry.eligibleVersions[0].statusLabel &&
                        ` · ${entry.eligibleVersions[0].statusLabel}`}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isGenerating}>
            Annuler
          </Button>
          <Button onClick={handleGenerate} disabled={!canGenerate}>
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Génération…
              </>
            ) : (
              'Générer le bordereau'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
